import { useMutation } from "@apollo/client";
import type React from "react";
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { v4 as uuid } from "uuid";
import { gql, Participant } from "@medbillai/generated-graphql-types";
import { retry } from "@medbillai/utils";
import { type DocumentScanEntryPoint, deriveBatchScanbotEntryPoint } from "@medbillai/utils/documentScan";
import { isArchiveFile, UNSUPPORTED_FILE_TYPE_MESSAGE } from "@medbillai/utils/files";
import { errorHandling } from "../../lib/apollo/utils";
import { singularService } from "../../lib/singular/singular";
import { SingularEvents } from "../../lib/singular/types";
const createMultiFileDocumentMutation = gql(/* GraphQL */`
  mutation CreateMultiFileDocument($input: CreateMultiFileDocumentInput!) {
    createMultiFileDocument(input: $input) {
      document {
        id
      }
      files {
        s3Key
        url
        name
      }
    }
  }
`);
const finalizeDocumentMutation = gql(/* GraphQL */`
  mutation FinalizeDocument($input: FinalizeDocumentInput!) {
    finalizeDocument(input: $input) {
      document {
        id
      }
    }
  }
`);
export type Upload = {
  id: string;
  documentId?: string;
  fileCount: number;
  contentType?: string;
};
export type UploadFile = {
  name: string;
  sizeBytes?: number;
  contentType: string;
  blob: Blob;
  // Surface the Scanbot scan was launched from. Absent for picked files —
  // the server scopes the scan-quality experiment's downstream guardrail
  // metrics on it, and anything it doesn't recognise is excluded.
  scanbotEntryPoint?: DocumentScanEntryPoint;
};
export type UploadInput = {
  key: string;
  uploadFiles: UploadFile[];
  issueId?: string;
  // Optional document type to skip ingestion classification.
  // Valid values defined in ts/apps/api/src/vision/types.ts (DocumentType).
  dataType?: string;
  // When set to false, the uploaded document won't appear in the chat timeline.
  userVisible?: boolean;
  // When set to false, the document is ingested without engaging the case
  // agent (no triage, no analysis chat replies). For uploads that are form
  // inputs rather than case activity — the FA wizard's document fields.
  startAnalysis?: boolean;
  onUploadComplete: (documentId: string) => Promise<unknown>;
  onUploadStarted: () => void;
  onUploadError: (error: UploadError) => void;
};
export interface UploadDocumentContextInterface {
  uploads: Record<string, Upload[]>;
  upload: (uploadInput: UploadInput) => Promise<void>;
}
export const initialContext: UploadDocumentContextInterface = {
  uploads: {},
  upload: () => {
    throw new Error("Context not initialized");
  }
};
export const UploadDocumentContext = createContext<UploadDocumentContextInterface>(initialContext);
export interface UploadDocumentProviderProps {
  children?: React.ReactNode;
}
export type UploadError = {
  uploadId?: string;
  // User-friendly message to display to the user.
  message?: string;
  // The actual error that occurred.
  error?: Error;
};
class S3UploadError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message);
    this.name = "S3UploadError";
  }
}
const isTransientS3Error = (error: unknown): boolean => error instanceof S3UploadError && (error.status === 503 || error.status === 500 || error.status === 429);
const uploadFileToS3WithRetry = async (url: string, blob: Blob, contentType: string, fileName: string): Promise<void> => {
  await retry(async () => {
    const response = await fetch(url, {
      method: "PUT",
      body: blob,
      headers: {
        "Content-Type": contentType
      }
    });
    if (!response.ok) {
      throw new S3UploadError(`S3 upload failed for ${fileName}: ${response.status}`, response.status);
    }
  }, {
    times: 3,
    delay: 1000,
    backoff: 2,
    shouldRetry: isTransientS3Error
  });
};

/*
 * A high level provider for document uploading
 * Its purpose is to:
 * - Keep track of the upload status of documents.
 * - Enable parallel uploads of multiple documents
 *   and ensure a process in the background even if
 *   the user navigates away from the screen where the upload
 *   is initiated.
 */
export const UploadDocumentProvider = ({
  children
}: UploadDocumentProviderProps) => {
  const [uploads, setUploads] = useState<Record<string, Upload[]>>({});
  const [createMultiFileDocument] = useMutation(createMultiFileDocumentMutation, {
    context: errorHandling("no-log"),
    ignoreResults: true
  });
  const [finalizeDocument] = useMutation(finalizeDocumentMutation, {
    context: errorHandling("no-log"),
    ignoreResults: true
  });

  // Careful here to only pass updater methods to the state `set`.
  // Each uploads calling this method closes over the current state
  // and we don't want to reset the state to a stale value.
  const addUpload = useCallback((newUpload: Upload, key: string) => {
    setUploads(curr => ({
      ...curr,
      [key]: [...(curr[key] ?? []), newUpload]
    }));
  }, []);
  const removeUpload = useCallback((uploadId: string, key: string) => {
    setUploads(curr => {
      const remainingUploadsForKey = (curr[key] ?? []).filter(upload => upload.id !== uploadId);
      if (remainingUploadsForKey.length === 0) {
        // Remove the key property from the object since there are no uploads left for this key.
        const rest = Object.fromEntries(Object.entries(curr).filter(([k]) => k !== key));
        return rest;
      }
      return {
        ...curr,
        [key]: remainingUploadsForKey
      };
    });
  }, []);
  const updateUpload = useCallback((uploadId: string, updates: Partial<Upload>, key: string) => {
    setUploads(curr => curr[key] ? {
      ...curr,
      [key]: curr[key].map(upload => upload.id === uploadId ? {
        ...upload,
        ...updates
      } : upload)
    } : curr);
  }, []);
  const upload = useCallback(async ({
    key,
    uploadFiles,
    issueId,
    dataType,
    userVisible,
    startAnalysis,
    onUploadStarted,
    onUploadComplete,
    onUploadError
  }: UploadInput) => {
    if (uploadFiles.length === 0) {
      throw new Error("No files to upload");
    }

    // Mirrors the server-side rejection in createMultiFileDocument, but
    // with `message` set so every consumer shows the reason instead of
    // its generic "try again" copy.
    if (uploadFiles.some(isArchiveFile)) {
      onUploadError({
        message: UNSUPPORTED_FILE_TYPE_MESSAGE
      });
      return;
    }
    onUploadStarted();
    const uploadId = uuid().toString();
    addUpload({
      id: uploadId,
      fileCount: uploadFiles.length,
      contentType: uploadFiles[0]?.contentType
    }, key);
    try {
      const {
        data
      } = await createMultiFileDocument({
        variables: {
          input: {
            // Guaranteed with the above check; default to first file name.
            name: uploadFiles[0]!.name,
            files: uploadFiles.map(file => ({
              name: file.name,
              sizeBytes: file.sizeBytes,
              contentType: file.contentType
            })),
            participant: Participant.USER,
            ...(dataType ? {
              dataType
            } : {})
          }
        }
      });
      if (!data?.createMultiFileDocument) {
        onUploadError({
          error: new Error("Unexpected empty document from server")
        });
        return;
      }
      const {
        document,
        files
      } = data.createMultiFileDocument;

      // Store the server-generated document ID in the upload record
      updateUpload(uploadId, {
        documentId: document.id
      }, key);
      for (const file of files) {
        const uploadFile = uploadFiles.find(f => f.name === file.name);
        await uploadFileToS3WithRetry(file.url, uploadFile!.blob, uploadFile!.contentType, file.name);
      }

      // Provenance rides on the files rather than the call site: a batch is
      // either all scanned or all picked, and every picker funnels through
      // here. Require unanimity so a future mixed batch degrades to excluded
      // rather than inheriting file[0]'s provenance.
      const scanbotEntryPoint = deriveBatchScanbotEntryPoint(uploadFiles.map(file => file.scanbotEntryPoint));
      await finalizeDocument({
        variables: {
          input: {
            documentId: document.id,
            issueId,
            ...(userVisible !== undefined ? {
              userVisible
            } : {}),
            ...(startAnalysis !== undefined ? {
              startAnalysis
            } : {}),
            ...(scanbotEntryPoint ? {
              scanbotEntryPoint
            } : {})
          }
        }
      });
      singularService.event(SingularEvents.documentUploaded);

      // Await to prevent finally from
      // removing upload before its returned from graphql
      // to improve loading of the new entry when changing from
      // upload -> event.
      await onUploadComplete(document.id);
    } catch (error) {
      if (error instanceof Error) {
        onUploadError({
          uploadId,
          error
        });
      } else {
        onUploadError({
          uploadId,
          error: new Error("Unknown error")
        });
      }
    } finally {
      removeUpload(uploadId, key);
    }
  }, [addUpload, createMultiFileDocument, finalizeDocument, removeUpload, updateUpload]);
  const contextValue = useMemo<UploadDocumentContextInterface>(() => ({
    uploads,
    upload
  }), [uploads, upload]);
  return <UploadDocumentContext.Provider value={contextValue} data-sentry-element="UploadDocumentContext.Provider" data-sentry-component="UploadDocumentProvider" data-sentry-source-file="UploadDocumentProvider.tsx">
      {children}
    </UploadDocumentContext.Provider>;
};
export const useDocumentUpload = (context = UploadDocumentContext): UploadDocumentContextInterface => useContext(context);